import { CONFIG as C } from '../config/config'

// ── Derived ───────────────────────────────────────────────────────────────────
export const totalEstHours  = (g) => (g.estimated_weeks ?? 0) * (g.weekly_hours ?? 0)
export const minRealistic   = (g, cap) => cap ? totalEstHours(g) / cap : null

// ── Time load ─────────────────────────────────────────────────────────────────
export const timeLoad       = (active) => active.reduce((s, g) => s + (g.weekly_hours ?? 0), 0)

export const timeStatus = (active, avail) => {
  if (!avail) return 'ok'
  const r = timeLoad(active) / avail
  if (r >= C.TIME_BLOCK_RATIO) return 'block'
  if (r >= C.TIME_WARN_RATIO)  return 'warn'
  return 'ok'
}

export const timeOverage = (active, avail) =>
  Math.max(0, timeLoad(active) - (avail ?? 0))

// ── Cognitive load ────────────────────────────────────────────────────────────
export const cogScore = (active) =>
  active.reduce((s, g) => s + (C.COGNITIVE_WEIGHTS[g.effort_level] ?? 0), 0)

export const cogFlagged = (active, cap) =>
  cogScore(active) > (C.COGNITIVE_THRESHOLDS[cap] ?? 9)

// ── Per-goal flags ────────────────────────────────────────────────────────────
export const goalFlags = (g, avail) => {
  const flags = []
  if (!g.estimated_weeks || !g.weekly_hours) return flags

  const total    = totalEstHours(g)
  const minWeeks = avail ? total / avail : null

  if (g.weekly_hours < 0.5)
    flags.push({ type: 'low_hours', msg: 'Weekly hours seem too low for meaningful progress.' })

  if (avail && g.weekly_hours / avail > C.SINGLE_GOAL_CAP)
    flags.push({ type: 'dominates', msg: `This goal consumes over ${Math.round(C.SINGLE_GOAL_CAP * 100)}% of your weekly capacity.` })

  if (minWeeks && g.estimated_weeks < minWeeks * C.COMPRESS_FACTOR)
    flags.push({ type: 'compressed', msg: `Duration looks compressed. Minimum realistic at your pace: ~${Math.ceil(minWeeks)} weeks.` })

  return flags
}

// ── Active cap ────────────────────────────────────────────────────────────────
export const atCap = (goals, cap) =>
  goals.filter(g => g.status === 'active').length >= (cap ?? 3)

// ── Cost of inaction ─────────────────────────────────────────────────────────
export const inactionMsg = (g, avail) => {
  if (!g.created_at || g.bucket !== 'aspirational') return null
  const weeksDeferred = Math.floor((Date.now() - new Date(g.created_at)) / 604800000)
  if (weeksDeferred < 4) return null

  const hrs    = totalEstHours(g) || (g.estimated_weeks ?? 0) * (avail ? avail / 2 : 5)
  const months = hrs && avail ? Math.max(1, Math.round(hrs / avail / 4)) : null

  return {
    weeksDeferred,
    msg: `Deferred ${weeksDeferred} weeks.${months ? ` Estimated viable window: ~${months} months.` : ''}`,
  }
}

// ── Full engine snapshot ──────────────────────────────────────────────────────
export const runEngine = ({ goals, avail, cap, weeksLeft }) => {
  const active = goals.filter(g => g.status === 'active')
  return {
    active,
    load:          timeLoad(active),
    status:        timeStatus(active, avail),
    overage:       timeOverage(active, avail),
    cogScore:      cogScore(active),
    cogFlagged:    cogFlagged(active, cap),
    atCap:         atCap(goals, cap),
    lifeFlagged:   active.reduce((s, g) => s + (g.estimated_weeks ?? 0), 0) > (weeksLeft ?? Infinity),
  }
}
